/* globals google, markerClusterer, deck */
;(function() {
  'use strict'

  Controller.$inject = ["$element", "$compile", "$scope", "$log", "mapService", "reportMapChartStyles"];
  angular.module('app.core').component('surveyExplorerMap', {
    controller: Controller,
    templateUrl: 'survey-explorer-map.html',
    bindings: {
      locationSet: '<',
      isMarkers: '<',
    },
  })

  /* @ngInject */
  function Controller(
    $element,
    $compile,
    $scope,
    $log,
    mapService,
    reportMapChartStyles
  ) {
    var ctrl = this
    $log = $log.create('surveyExplorerMap')

    var map
    var deckOverlay
    var markerCluster
    var infoWindow
    var infoWindowScope
    var heatmapData = []
    var markers = []
    var clusterInfoCache = {}

    // Bumped on every render and on destroy.
    // Each mode's async library load checks it,
    // so a late callback never renders after the user
    // switched mode, switched view, or left the map.
    var renderToken = 0

    var MAX_ZOOM_LEVEL = 21

    var mapOptions = {
      center: { lat: -34.397, lng: 150.644 }, // arbitrary, doesnt matter
      zoom: 8,
      maxZoom: MAX_ZOOM_LEVEL,
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: reportMapChartStyles,
    }

    ctrl.$onInit = onInit
    ctrl.$onChanges = onChanges
    ctrl.$onDestroy = onDestroy

    function onInit() {
      // only Google Maps is needed up front; the clusterer and deck.gl are
      // loaded lazily by whichever mode actually renders (see updateMapView).
      mapService.load().then(function() {
        // create map and add click listener
        var mapElement = $element[0].querySelector('.survey-explorer-map__map')
        map = new google.maps.Map(mapElement, mapOptions)
        map.addListener('click', onMapClick)

        // create a new isolated scope for the InfoWindow content
        infoWindowScope = $scope.$new(true)
        infoWindowScope.zoom = function(clusterBounds) {
          map.fitBounds(clusterBounds)
        }

        // Compile the template once and link it to the scope
        var templateHtml = getInfoWindowTemplate()
        var compiledTemplate = $compile(templateHtml)(infoWindowScope)

        // initialise an info window
        infoWindow = new google.maps.InfoWindow({
          content: compiledTemplate[0],
          maxWidth: 300,
          headerDisabled: true,
        })

        populateMapData()
        updateMapView()
      })
    }

    function onChanges(changes) {
      if (!map) return
      if (changes.locationSet) {
        populateMapData()
      }
      updateMapView()
    }

    function onDestroy() {
      // invalidate any in-flight render and free the overlay's WebGL context
      renderToken++
      if (deckOverlay) {
        deckOverlay.setMap(null)
        deckOverlay = null
      }
    }

    function populateMapData() {
      // reset cache and data arrays
      clusterInfoCache = {}
      heatmapData = []
      markers = []

      // get a fresh bounds
      var bounds = makeBaseBounds()

      ctrl.locationSet.locations.forEach(function(location) {
        var locationLatlng = location[ctrl.locationSet.Type.LATLNG]
        var locationName = location[ctrl.locationSet.Type.NAME]
        var ll = locationLatlng.split(',')

        // Skip invalid data and log the error
        if (isNaN(ll[0]) || isNaN(ll[1])) {
          $log.error(
            `Missing latlng data (${locationLatlng}) for location ${locationName}. ResponseId: ${location.interactionId}`
          )
          return
        }

        // create latlng object
        var latlng = new google.maps.LatLng(ll[0], ll[1])

        // extend map bounds incase we have locations outside of Australia
        bounds.extend(latlng)

        // add to heatmap data (deck.gl expects positions as [lng, lat])
        heatmapData.push({ position: [parseFloat(ll[1]), parseFloat(ll[0])] })

        // create marker and add to markers array
        var marker = new google.maps.Marker({
          position: latlng,
          title: locationName,
          optimized: true,
        })
        marker.addListener('click', onMarkerClick)
        markers.push(marker)
      })

      // zoom to fit all points
      map.fitBounds(bounds)

      // dont zoom in too far
      var zoom = _.clamp(map.getZoom(), 0, 10)
      map.setZoom(zoom)
    }

    function updateMapView() {
      // invalidate any in-flight render from a previous mode/view
      var token = ++renderToken

      // clear whatever the previous render left on the map
      if (markerCluster) markerCluster.clearMarkers()
      if (deckOverlay) deckOverlay.setMap(null)
      infoWindow.close()

      // load only the library this mode needs, then render the current data.
      // the token guard drops the render if the mode/view changed (or the
      // component was destroyed) while the library was loading.
      if (ctrl.isMarkers) {
        mapService.loadMarkerClusterer().then(function() {
          if (token !== renderToken) return
          renderMarkers()
        })
      } else {
        mapService.loadDeckGl().then(function() {
          if (token !== renderToken) return
          renderHeatmap()
        })
      }
    }

    function renderMarkers() {
      // create the clusterer on first use (kept and reused across renders)
      if (!markerCluster) {
        markerCluster = new markerClusterer.MarkerClusterer({
          map: map,
          // set max zoom level to prevent cluster from disappearing
          // when multiple markers are at the same position.
          algorithmOptions: { maxZoom: MAX_ZOOM_LEVEL },
          onClusterClick: onClusterClick,
        })
      }
      markerCluster.addMarkers(markers)
    }

    function renderHeatmap() {
      var layer = new deck.HeatmapLayer({
        id: 'heatmap',
        data: heatmapData,
        getPosition: function(d) {
          return d.position
        },
        getWeight: 1,
        radiusPixels: 30,
      })

      // a fresh overlay each render is the simplest reliable way to paint the
      // current data (a reused overlay does not repaint on a mode toggle).
      deckOverlay = new deck.GoogleMapsOverlay({ layers: [layer] })
      deckOverlay.setMap(map)
    }

    function onMapClick(event) {
      infoWindow.close()
    }

    function onMarkerClick(event) {
      var marker = this
      var count = 1
      var info = {
        type: 'marker',
        title: marker.getTitle(),
        count: count,
        percent: calcPercent(count),
      }
      showInfoWindow(marker, info)
    }

    function onClusterClick(event, cluster, map) {
      if (!cluster.key) {
        cluster.key = generateClusterKey(cluster)
      }

      var info = clusterInfoCache[cluster.key]
      if (!info) {
        var items = _(cluster.markers)
          .groupBy(function(marker) {
            return marker.getPosition().toUrlValue()
          })
          .map(function(group) {
            return {
              title: group[0].getTitle(),
              count: group.length,
              percent: calcPercent(group.length),
            }
          })
          .orderBy(['count'], ['desc'])
          .value()

        info = clusterInfoCache[cluster.key] = {
          type: 'cluster',
          count: cluster.count,
          percent: calcPercent(cluster.count),
          items: items,
          clusterBounds: cluster.bounds,
        }
      }
      showInfoWindow(cluster.marker, info)
    }

    function generateClusterKey(cluster) {
      // since clusters don't have IDs and are dynamically created,
      // we need to create our own unique key for each cluster so that we can cache them.
      var clusterPos = cluster.position.toUrlValue()
      var clusterCount = cluster.count
      var firstMarkerPos = _.first(cluster.markers)
        .getPosition()
        .toUrlValue()
      var lastMarkerPos = _.last(cluster.markers)
        .getPosition()
        .toUrlValue()
      return `${clusterPos}|${clusterCount}|${firstMarkerPos}|${lastMarkerPos}`
    }

    function calcPercent(count) {
      return ((count / markers.length) * 100).toFixed(2)
    }

    function showInfoWindow(marker, info) {
      // close infowindow if the same marker is clicked
      if (
        infoWindow.isOpen &&
        infoWindow.getPosition().equals(marker.getPosition())
      ) {
        return infoWindow.close()
      }

      // update infowindow scope and apply the changes
      infoWindowScope.info = info
      infoWindowScope.$apply()

      // open infowindow
      infoWindow.open({ anchor: marker })
    }

    function getInfoWindowTemplate() {
      return `
      <div class="survey-explorer-map__info">
        <div ng-if="info.type === 'cluster'">
          <div ng-if="info.items.length > 1" class="survey-explorer-map__info-header">
            <div class="survey-explorer-map__info-text -title">
              <span>Total (n) in this area</span>
              <span>{{ info.percent }}% ({{ info.count }})</span>
            </div>
          </div>
          <div class="survey-explorer-map__info-list">
            <div ng-repeat="item in info.items" class="survey-explorer-map__info-text">
              <span>{{ item.title }}</span>
              <span>{{ item.percent }}% ({{ item.count }})</span>
            </div>
          </div>
          <div ng-if="info.items.length > 1" class="survey-explorer-map__info-footer">
            <g-btn label="Zoom In" tertiary="true" small="true" on-click="zoom(info.clusterBounds)"></g-btn>
          </div>
        </div>
        <div ng-if="info.type === 'marker'" class="survey-explorer-map__info-text">
          <span>{{ info.title }}</span>
          <span>{{ info.percent }}% ({{ info.count }})</span>
        </div>
      </div>
      `
    }

    function makeBaseBounds() {
      var bounds = new google.maps.LatLngBounds()
      // our base bounds start with Australia (TODO: is this necessary?)
      bounds.extend(new google.maps.LatLng(-11.885944, 112.773279)) // top left of australia
      bounds.extend(new google.maps.LatLng(-44.657796, 154.162186)) // bottom right of australia
      return bounds
    }
  }
})()
